import React, { forwardRef } from 'react'
import { SelectProps, BoxProps } from '@chakra-ui/core'

import { Box } from '../Box'
import { TextInput, TextInputProps } from '../TextInput'
import { Icon } from '../Icon'
import { splitStyleProps } from '../../utils/splitStyleProps'

export interface SelectInputOption {
  label: string
  value: any
}

/////////////////////////////////////////////////////////////////////////////////////////////////

const IconWrapper = forwardRef((props: BoxProps, ref) => (
  <Box
    ref={ref}
    position="absolute"
    display="inline-flex"
    width="1.5rem"
    height="100%"
    alignItems="center"
    justifyContent="center"
    right="0.5rem"
    top="50%"
    pointerEvents="none"
    zIndex={2}
    transform="translateY(-50%)"
    {...props}
  />
))

/////////////////////////////////////////////////////////////////////////////////////////////////

interface InputProps extends TextInputProps {
  options?: (string | SelectInputOption)[]
}

const Input = forwardRef(({ placeholder, children, options, ...restProps }: InputProps, ref) => (
  <TextInput
    as="select"
    appearance="none"
    ref={ref}
    pr="2rem"
    pb="px"
    lineHeight="normal"
    {...restProps}
  >
    {placeholder && <option value="">{placeholder}</option>}
    {Array.isArray(options)
      ? options.map(item => (
          <option value={typeof item === 'string' ? item : item.value}>
            {typeof item === 'string' ? item : item.label}
          </option>
        ))
      : children}
  </TextInput>
))

/////////////////////////////////////////////////////////////////////////////////////////////////

export interface SelectInputProps extends SelectProps {
  options?: (string | SelectInputOption)[]
  iconColor?: string
  iconSize?: string
  icon?: string
  onChangeValue?: (value: SelectInputOption['value']) => void
}

export const SelectInput = forwardRef(
  (
    {
      rootProps,
      icon,
      iconSize = '20px',
      iconColor,
      onChange,
      onChangeValue,
      ...props
    }: SelectInputProps,
    ref,
  ) => {
    const opacity = props.isReadOnly || props.isDisabled ? 0.5 : null

    const [root, select] = splitStyleProps(props)

    const handleChange = event => {
      onChange && onChange(event)
      onChangeValue && onChangeValue(event.target.value)
    }

    return (
      <Box position="relative" width="100%" {...root} {...rootProps}>
        <Input ref={ref} onChange={handleChange} {...select} />
        <IconWrapper opacity={opacity} color={iconColor || select.color}>
          <Icon
            focusable={false}
            aria-hidden="true"
            name={icon || 'chevron-down'}
            size={iconSize}
          />
        </IconWrapper>
      </Box>
    )
  },
)

SelectInput.displayName = 'Select'

export default SelectInput
